/**
 * CoachAthleteOverview — Coach view showing all athletes' SmartBlock data.
 * Table with name, latest score, momentum, CSK mini-bars, and cluster count.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getCoachAthletes,
  getTrackerTypes,
} from '@/lib/smartblocksApi';
import type { CoachAthleteData, TrackerType } from '@/lib/smartblocksApi';

type CoachAthleteOverviewProps = {
  trackerTypeId: string;
};

export function CoachAthleteOverview({ trackerTypeId }: CoachAthleteOverviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [athletes, setAthletes] = useState<CoachAthleteData[]>([]);
  const [trackerType, setTrackerType] = useState<TrackerType | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [types, data] = await Promise.all([
        getTrackerTypes(),
        getCoachAthletes(trackerTypeId),
      ]);
      setTrackerType(types.find((t) => t.id === trackerTypeId) ?? null);
      setAthletes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load athlete data');
    } finally {
      setLoading(false);
    }
  }, [trackerTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-cocoa">
          {trackerType?.label || 'Tracker'} — Roster
        </h2>
        <p className="text-sm text-cocoa/60">
          {athletes.length} athlete{athletes.length !== 1 ? 's' : ''} enrolled
        </p>
      </div>

      {athletes.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <p className="text-sm text-cocoa/60">
            No athletes enrolled in this tracker yet.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-4 py-3 font-medium text-cocoa/70">Athlete</th>
                <th className="px-4 py-3 font-medium text-cocoa/70 text-center">Score</th>
                <th className="px-4 py-3 font-medium text-cocoa/70 text-center">Momentum</th>
                <th className="hidden px-4 py-3 font-medium text-cocoa/70 text-center sm:table-cell">
                  CSK
                </th>
                <th className="px-4 py-3 font-medium text-cocoa/70 text-center">Clusters</th>
              </tr>
            </thead>
            <tbody>
              {athletes
                .sort((a, b) => {
                  const aScore = a.latest_block?.gec0 ?? -1;
                  const bScore = b.latest_block?.gec0 ?? -1;
                  return bScore - aScore;
                })
                .map((athlete) => (
                  <AthleteRow key={athlete.user_id} athlete={athlete} />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AthleteRow({ athlete }: { athlete: CoachAthleteData }) {
  const block = athlete.latest_block;
  const scorePercent = block ? Math.round(block.gec0 * 100) : null;

  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50/50">
      {/* Athlete name */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {athlete.avatar_url ? (
            <img
              src={athlete.avatar_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">
              {athlete.name.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-medium text-cocoa">{athlete.name}</span>
        </div>
      </td>

      {/* Score */}
      <td className="px-4 py-3 text-center">
        {scorePercent !== null ? (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
              scorePercent >= 70
                ? 'bg-emerald-100 text-emerald-700'
                : scorePercent >= 45
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
            }`}
          >
            {scorePercent}%
          </span>
        ) : (
          <span className="text-xs text-cocoa/40">—</span>
        )}
      </td>

      {/* Momentum */}
      <td className="px-4 py-3 text-center">
        {block ? (
          <span
            className={`text-xs font-medium ${
              block.momentum >= 0 ? 'text-emerald-600' : 'text-red-500'
            }`}
          >
            {block.momentum >= 0 ? '+' : ''}
            {Math.round(block.momentum * 100)}%
          </span>
        ) : (
          <span className="text-xs text-cocoa/40">—</span>
        )}
      </td>

      {/* CSK mini bars */}
      <td className="hidden px-4 py-3 sm:table-cell">
        {block?.csk_factors ? (
          <div className="flex items-center justify-center gap-0.5">
            {(['S', 'H', 'D', 'R', 'E'] as const).map((factor) => {
              const val = block.csk_factors[factor] ?? 0;
              return (
                <div key={factor} className="flex flex-col items-center" title={`${factor}: ${Math.round(val * 100)}%`}>
                  <div className="h-6 w-2 overflow-hidden rounded-sm bg-slate-200">
                    <div
                      className="w-full rounded-sm bg-emerald-500 transition-all"
                      style={{ height: `${val * 100}%`, marginTop: `${(1 - val) * 100}%` }}
                    />
                  </div>
                  <span className="mt-0.5 text-[8px] text-cocoa/40">{factor}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-cocoa/40">—</span>
        )}
      </td>

      {/* Clusters */}
      <td className="px-4 py-3 text-center">
        <span className="text-sm text-cocoa">{athlete.cluster_count}</span>
      </td>
    </tr>
  );
}

export default CoachAthleteOverview;
